// Vue 3 replacement for the removed `this.$children`.
//
// `$children` was removed in Vue 3 and @vue/compat's INSTANCE_CHILDREN shim
// is unreliable / disabled in recent versions ("INSTANCE_CHILDREN compat has
// been disabled"). This walks a component's internal render subtree and
// returns its DIRECT child component public instances (proxies) — the same
// set Vue 2's `$children` exposed for the tree-walking call sites that need
// to locate a sibling/descendant component instance.
export function getComponentChildren(vm) {
  // Test mocks set `$children` as a plain own array property. The real Vue 3
  // public instance instead resolves `$children` through the @vue/compat
  // getter, which THROWS "INSTANCE_CHILDREN compat has been disabled" the
  // moment it is read — so we must never touch `vm.$children` directly.
  // `getOwnPropertyDescriptor` hits the proxy's own-descriptor trap (not the
  // throwing `get` trap): a data descriptor with an array `value` means a
  // mock; `undefined` (Vue 3 dynamic prop) falls through to the subtree walk.
  try {
    const ownChildren = vm && Object.getOwnPropertyDescriptor(vm, '$children');
    if (ownChildren && Array.isArray(ownChildren.value)) {
      return ownChildren.value;
    }
  } catch (error) {
    // Any probe failure -> use the runtime subtree walk below.
  }

  const internal = vm && vm.$;
  if (!internal || !internal.subTree) {
    return [];
  }

  const result = [];
  const walk = (vnode) => {
    if (!vnode || typeof vnode !== 'object') {
      return;
    }
    // A vnode with `.component` IS a child component; collect its public
    // proxy and stop (its own subtree = grandchildren, not direct children).
    if (vnode.component) {
      if (vnode.component.proxy) {
        result.push(vnode.component.proxy);
      }
      return;
    }
    const { children } = vnode;
    if (Array.isArray(children)) {
      children.forEach(walk);
    }
  };

  walk(internal.subTree);
  return result;
}

export default getComponentChildren;
